/**
 * Normaliza `response.data.message` do NestJS (string, array ou objeto por campo).
 */
export function normalizarMensagemErroApi(mensagem: unknown, fallback = 'Erro desconhecido'): string {
  if (mensagem == null) return fallback;

  if (typeof mensagem === 'string') {
    const texto = mensagem.trim();
    return texto || fallback;
  }

  if (typeof mensagem === 'number' || typeof mensagem === 'boolean') {
    return String(mensagem);
  }

  if (Array.isArray(mensagem)) {
    const partes = mensagem.flatMap((item) => {
      const texto = normalizarMensagemErroApi(item, '');
      return texto ? [texto] : [];
    });
    return partes.length > 0 ? partes.join('\n') : fallback;
  }

  if (typeof mensagem === 'object') {
    const obj = mensagem as Record<string, unknown>;

    if ('message' in obj && obj.message !== mensagem) {
      return normalizarMensagemErroApi(obj.message, fallback);
    }

    const partes = Object.entries(obj).flatMap(([chave, valor]) => {
      if (chave === 'error' || chave === 'statusCode') return [];
      const texto = normalizarMensagemErroApi(valor, '');
      return texto ? [`${chave}: ${texto}`] : [];
    });

    return partes.length > 0 ? partes.join('\n') : fallback;
  }

  return fallback;
}

/**
 * Mapeamento de nomes de tabelas para nomes amigáveis em português
 */
const tabelaNomes: Record<string, string> = {
  estagiario: 'estagiário',
  colaborador: 'colaborador',
  defensor: 'defensor',
  usuario: 'usuário',
  pessoa: 'pessoa',
  lotacao: 'lotação',
  defensoria: 'defensoria',
  nucleo: 'núcleo',
  setor: 'setor',
  predio: 'prédio',
  comarca: 'comarca',
  vara: 'vara',
  diretoria: 'diretoria',
  funcao: 'função',
  periodo: 'período',
  movimentacao: 'movimentação',
  gasto: 'gasto',
  tipo_gasto: 'tipo de gasto',
};

/**
 * Converte o nome de uma tabela para um nome amigável em português
 */
function obterNomeAmigavelTabela(nomeTabela: string): string {
  const nomeLower = nomeTabela.toLowerCase();
  return tabelaNomes[nomeLower] || nomeTabela;
}

/**
 * Extrai informações de uma constraint de chave estrangeira do erro
 * Exemplos de mensagens:
 * - "atualização ou exclusão em tabela \"pessoa\" viola restrição de chave estrangeira \"FK_estagiario_pessoa\" em \"estagiario\""
 * - "update or delete on table \"pessoa\" violates foreign key constraint \"FK_estagiario_pessoa\" on table \"estagiario\""
 */
function extrairInfoChaveEstrangeira(mensagem: string): { tabelaOrigem: string; tabelaDestino: string } | null {
  // Padrão para detectar constraint de chave estrangeira
  const padraoFK = /FK_(\w+)_(\w+)/i;
  const matchFK = mensagem.match(padraoFK);
  
  if (matchFK) {
    // FK_estagiario_pessoa -> tabela origem: estagiario, tabela destino: pessoa
    const tabelaOrigem = matchFK[1].toLowerCase();
    const tabelaDestino = matchFK[2].toLowerCase();
    return { tabelaOrigem, tabelaDestino };
  }

  // Tentar extrair das aspas se o padrão FK_ não funcionar
  const padraoAspas = /em (?:tabela )?"(\w+)"[^"]*"(\w+)"/i;
  const matchAspas = mensagem.match(padraoAspas);
  
  if (matchAspas) {
    const tabelaDestino = matchAspas[1].toLowerCase();
    const tabelaOrigem = matchAspas[2].toLowerCase();
    return { tabelaOrigem, tabelaDestino };
  }

  return null;
}

/**
 * Infere a ação (excluir/atualizar) da mensagem de erro
 */
function inferirAcao(mensagemErro: string): string {
  const mensagemLower = mensagemErro.toLowerCase();
  if (mensagemLower.includes('exclusão') || mensagemLower.includes('delete')) {
    return 'excluir';
  }
  if (mensagemLower.includes('atualização') || mensagemLower.includes('update')) {
    return 'atualizar';
  }
  return 'excluir'; // padrão
}

/**
 * Converte uma mensagem de erro de chave estrangeira em uma mensagem amigável
 * @param mensagemErro Mensagem de erro original
 * @param acao Ação que estava sendo executada (ex: "excluir", "atualizar") - opcional, será inferida se não fornecida
 * @param entidade Nome da entidade sendo manipulada (ex: "pessoa", "defensor") - opcional, será inferida se não fornecida
 * @returns Mensagem amigável ou a mensagem original se não for erro de FK
 */
export function formatarErroChaveEstrangeira(
  mensagemErro: unknown,
  acao?: string,
  entidade?: string
): string {
  const mensagem = normalizarMensagemErroApi(mensagemErro, '');
  if (!mensagem) return 'Erro desconhecido';

  // Verifica se é um erro de chave estrangeira
  const temViolacaoFK = 
    mensagem.toLowerCase().includes('viola restrição de chave estrangeira') ||
    mensagem.toLowerCase().includes('violates foreign key constraint') ||
    mensagem.toLowerCase().includes('fk_');

  if (!temViolacaoFK) {
    return mensagem;
  }

  const infoFK = extrairInfoChaveEstrangeira(mensagem);
  
  if (!infoFK) {
    // Se não conseguir extrair, retorna mensagem genérica
    const acaoFinal = acao || inferirAcao(mensagem);
    const entidadeFinal = entidade || 'registro';
    return `Não é possível ${acaoFinal} ${entidadeFinal} pois existem registros associados a ele.`;
  }

  const { tabelaOrigem, tabelaDestino } = infoFK;
  const nomeOrigemAmigavel = obterNomeAmigavelTabela(tabelaOrigem);
  const nomeDestinoAmigavel = entidade || obterNomeAmigavelTabela(tabelaDestino);
  const acaoFinal = acao || inferirAcao(mensagem);

  // Usa uma construção mais natural que evita problemas com artigos
  return `Não é possível ${acaoFinal} ${nomeDestinoAmigavel} pois existem registros de ${nomeOrigemAmigavel} associados a ele.`;
}
